/**
 * JourneyEntryModal — create/edit a client-journey entry (team side).
 *
 * Opened via CustomEvent('open-journey-entry-modal', { detail: {
 *   journeyId, entry?, presetType?, presetMilestoneId?, presetMeetingId?,
 *   presetPhase?, presetTitle?, presetData?
 * }}). On save it upserts the entry and dispatches 'journey-updated' { journeyId }
 * so the JourneyAuthorSection reloads.
 *
 * Per-type fields come from the module registry (single source of truth). The TYPE PICKER is
 * narrower on purpose (Matt, 2026-10-15): it offers only ADDABLE_TYPES, the things a person adds
 * by hand. Everything else reaches the hub pushed from where it lives (post a milestone or
 * meeting, send a proposal or ASR from the CRM, the punch list, permitting), so it is never
 * pulled in from here. The registry keeps every type so existing entries still render and edit.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { MODULE_REGISTRY, getModule } from '../journey/modules/index.js'
import { upsertEntry, getOrCreateLink, getWalkthroughModels, uploadHubDocument } from '../services/journey.js'
import SearchSelect from './SearchSelect.jsx'
import { logClientHubAction } from '../services/threads.js'
import ContactPicker from './crm/ContactPicker.jsx'
import OrganizationPicker from './crm/OrganizationPicker.jsx'
import { getContactDetail, getOrganizationDetail, getOrgNamesByIds } from '../services/contacts.js'
import SketchManager from '../journey/SketchManager.jsx'
import { getProjectPhases, matchPhaseName } from '../services/phases.js'
import { getProjectMeetings } from '../services/meetings.js'
import { isMilestonePosted } from '../journey/eligibility.js'

// The only types a person picks in "Add entry". Keep in sync with the copy on the morning
// check's "Add to Client Hub…" button (TriageGate.jsx).
// 'client_release' replaced 'walkthrough' here 2026-10-22 (Matt): picking it opens AlderCAD on the
// release form; the card itself is posted from there (api/_clientRelease.js).
const ADDABLE_TYPES = ['sketches', 'document', 'client_release', 'contact_card', 'onboarding-questionnaire', 'reference']

const getFirst = (v) => Array.isArray(v) ? v[0] : v
function cleanName(v) {
  if (Array.isArray(v)) return v[0] || ''
  if (typeof v === 'string') {
    const s = v.trim()
    if (s.startsWith('[')) { try { const a = JSON.parse(s); return Array.isArray(a) ? (a[0] || '') : s } catch { /* ignore */ } }
    return s
  }
  return ''
}

/* The "PDF document" entry's file (docs/CLIENT_HUB_PDF_DOCUMENTS.md). One PDF, sent as it is. Picking
   a file uploads it at once; a blank title takes the file's name. Replacing keeps the entry. */
function PdfDocumentField({ value, journeyId, title, onTitle, onChange }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [progress, setProgress] = useState('')
  async function pick(e) {
    const f = (e.target.files || [])[0]
    e.target.value = ''
    if (!f) return
    if (!/\.pdf$/i.test(f.name) && f.type !== 'application/pdf') { setErr('That is not a PDF.'); return }
    if (!journeyId) { setErr('The Client Hub is still opening. Try again in a moment.'); return }
    setBusy(true); setErr(''); setProgress('Uploading…')
    try {
      const pdf = await uploadHubDocument(journeyId, f, (done, total) => setProgress('Preparing pages for the Hub: ' + done + ' of ' + total))
      onChange(pdf)
      if (!String(title || '').trim()) onTitle(f.name.replace(/\.pdf$/i, ''))
    } catch (ex) { setErr(ex.message || 'The upload did not finish.') }
    finally { setBusy(false); setProgress('') }
  }
  const mb = value && value.size ? (value.size / 1048576).toFixed(1) + ' MB' : ''
  return (
    <div>
      {value && value.url ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-subtle)' }}>
          <a href={value.url} target="_blank" rel="noopener noreferrer" style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: 'var(--aj-text)' }}>{value.name || 'Document.pdf'}</a>
          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', flexShrink: 0 }}>{[value.pages ? value.pages + ' pp' : '', mb].filter(Boolean).join(' · ')}</span>
          <label style={{ fontSize: 12, color: 'var(--aj-accent)', cursor: busy ? 'default' : 'pointer', flexShrink: 0 }}>
            {busy ? 'Working…' : 'Replace'}
            <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={busy} onChange={pick} />
          </label>
        </div>
      ) : (
        <label style={{ display: 'block', padding: '14px 10px', borderRadius: 4, border: '0.5px dashed var(--aj-border-mid)', textAlign: 'center', fontSize: 13, color: 'var(--aj-text-mid)', cursor: busy ? 'default' : 'pointer' }}>
          {busy ? (progress || 'Uploading…') : 'Choose a PDF'}
          <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={busy} onChange={pick} />
        </label>
      )}
      <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 6 }}>
        {busy && value && value.url ? progress : 'The client views it page by page on the Hub and can download it. Each page is saved as a picture for quick viewing; a long document takes a minute.'}
      </div>
      {err && <div style={{ fontSize: 12, color: '#B3261E', marginTop: 6 }}>{err}</div>}
    </div>
  )
}

/* The "Model walkthrough" entry's model picker (Matt, 2026-10-15). Lists the project's saved AlderCAD
   models by concept, selected concept first and pre-picked. Once released, offers to move the
   client's walkthrough to the model as it is now (data.refresh, acted on server-side at save). */
function WalkthroughModelField({ data, projectId, onChange }) {
  const [models, setModels] = useState(null)
  useEffect(() => {
    let alive = true
    if (!projectId) { setModels([]); return }
    getWalkthroughModels(projectId).then(r => { if (alive) setModels((r && r.models) || []) }).catch(() => { if (alive) setModels([]) })
    return () => { alive = false }
  }, [projectId])
  useEffect(() => {
    if (models && models.length && !data.modelId) onChange({ modelId: models[0].modelId, conceptName: models[0].conceptName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models])
  if (models === null) return <div style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading models…</div>
  if (!models.length) return <div style={{ fontSize: 12, color: '#B3261E' }}>This project has no saved AlderCAD model yet.</div>
  const released = data.releasedAt ? new Date(data.releasedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
  return (
    <div>
      <SearchSelect value={data.modelId || ''} onChange={v => onChange({ modelId: v, conceptName: (models.find(m => m.modelId === v) || {}).conceptName || '' })}
        style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }}
        options={models.map(m => ({ value: m.modelId, label: m.conceptName + (m.selected ? ' (selected concept)' : '') + ' · saved ' + new Date(m.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }))} />
      <div style={{ fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 6 }}>
        {released
          ? 'Released ' + released + '. The client sees the model as it was then.'
          : 'Publishing this entry releases the model as it is right now. Leave it as a draft to release later.'}
      </div>
      {released && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!data.refresh} onChange={e => onChange({ refresh: e.target.checked })} />
          Update the client's walkthrough to the model as it is now
        </label>
      )}
    </div>
  )
}

/* "Plans & 3D model" from the Hub (Matt, 2026-10-22): the plans are drawn by AlderCAD's own engine,
   so the Hub can't make the release itself. It picks the model (selected concept first) and the
   phase, then opens AlderCAD straight on Releases → Client Hub with that phase set. */
function ReleaseModelField({ data, projectId, onChange }) {
  const [models, setModels] = useState(null)
  useEffect(() => {
    let alive = true
    if (!projectId) { setModels([]); return }
    getWalkthroughModels(projectId).then(r => { if (alive) setModels((r && r.models) || []) }).catch(() => { if (alive) setModels([]) })
    return () => { alive = false }
  }, [projectId])
  useEffect(() => {
    if (models && models.length && !data.modelId) onChange({ modelId: models[0].modelId, conceptId: models[0].conceptId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models])
  if (models === null) return <div style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading models…</div>
  if (!models.length) return <div style={{ fontSize: 12, color: '#B3261E' }}>This project has no saved AlderCAD model yet.</div>
  return (
    <SearchSelect value={data.modelId || ''} onChange={v => onChange({ modelId: v, conceptId: (models.find(m => m.modelId === v) || {}).conceptId || '' })}
      style={{ width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }}
      options={models.map(m => ({ value: m.modelId, label: m.conceptName + (m.selected ? ' (selected concept)' : '') + ' · saved ' + new Date(m.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }))} />
  )
}

/* The "Share a contact" entry's contact field (Matt, 2026-10-15): a PERSON, or an ORGANIZATION
   with its main contact. Picking fills a snapshot (what the client sees and what goes in the
   vCard); every line stays editable here, so a missing company or a better number can be fixed
   without touching the Contacts record.
     data.kind    'person' (default) | 'org'
     data.contact the person, or the organization's main contact (optional for an org)
     data.org     { orgId, name, phone, email, website, street, city, state, zip } (org only) */
const PERSON_LINES = [
  ['name', 'Name'], ['title', 'Job title'], ['company', 'Company'], ['email', 'Email'], ['phone', 'Phone'],
  ['street', 'Street address'], ['city', 'City'], ['state', 'State'], ['zip', 'Zip'], ['website', 'Website'],
]
const MAIN_CONTACT_LINES = [['name', 'Name'], ['title', 'Job title'], ['email', 'Email'], ['phone', 'Phone']]
const ORG_LINES = [
  ['name', 'Organization name'], ['phone', 'Phone'], ['email', 'General email'], ['website', 'Website'],
  ['street', 'Street address'], ['city', 'City'], ['state', 'State'], ['zip', 'Zip'],
]
const WIDE = new Set(['street', 'website', 'name'])

function personFromRecord(id, f, company) {
  return {
    contactId: id,
    name: f['Name'] || [f['First Name'], f['Last Name']].filter(Boolean).join(' '),
    firstName: f['First Name'] || '', lastName: f['Last Name'] || '',
    title: f['Job Title'] || '', company: company || '',
    email: f['Email'] || '', phone: f['Phone'] || '',
    street: f['Street Address'] || '', city: f['City'] || '', state: f['State'] || '', zip: f['Zip'] || '',
    website: '',
  }
}

function LineInputs({ lines, value, onChange, inputStyle }) {
  const v = value || {}
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
      {lines.map(([k, lbl]) => (
        <input key={k} value={v[k] || ''} placeholder={lbl}
          onChange={e => onChange({ ...v, [k]: e.target.value, ...(k === 'name' ? { firstName: '', lastName: '' } : null) })}
          style={{ ...inputStyle, gridColumn: WIDE.has(k) ? '1 / -1' : undefined }} />
      ))}
    </div>
  )
}

function ContactCardField({ data, onPatch, inputStyle, menuZIndex }) {
  const kind = data.kind === 'org' ? 'org' : 'person'
  const c = data.contact || null
  const o = data.org || null
  const [loading, setLoading] = useState('')
  const seg = (on) => ({ padding: '5px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, cursor: 'pointer',
    border: '0.5px solid var(--aj-border-mid)', background: on ? 'var(--aj-text)' : 'var(--aj-bg-subtle)', color: on ? 'var(--aj-bg-surface)' : 'var(--aj-text)' })
  const sub = { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', margin: '12px 0 4px' }

  async function pickPerson(id, name, rec) {
    if (!id) { onPatch({ contact: null }); return }
    const f = (rec && rec.fields) || {}
    onPatch({ contact: { ...personFromRecord(id, f, ''), name: name || personFromRecord(id, f).name } })
    // Fill Company from the person's organization (the search result carries no org name).
    try {
      const full = await getContactDetail(id)
      const orgId = full && full.fields && (full.fields['Organizations'] || full.fields['Organization Contacts'] || [])[0]
      if (orgId) {
        const names = await getOrgNamesByIds([orgId])
        if (names[orgId]) onPatch(prev => ({ contact: prev.contact && prev.contact.contactId === id && !prev.contact.company ? { ...prev.contact, company: names[orgId] } : prev.contact }))
      }
    } catch { /* company stays blank, typed by hand */ }
  }

  async function pickOrg(id) {
    if (!id) { onPatch({ org: null, contact: null }); return }
    setLoading('Loading the organization…')
    try {
      const rec = await getOrganizationDetail(id)
      const f = (rec && rec.fields) || {}
      const org = {
        orgId: id, name: f['Organization Name'] || '', phone: f['Phone'] || '', email: f['General Email'] || '',
        website: f['Website'] || '', street: f['Client Address'] || '', city: f['Client City'] || '',
        state: f['Client State'] || '', zip: f['Client Zip'] || '',
      }
      let main = null
      const mainId = (f['Primary Contact'] || [])[0]
      if (mainId) {
        try { const pr = await getContactDetail(mainId); if (pr && pr.fields) main = personFromRecord(mainId, pr.fields, org.name) } catch { /* no main contact */ }
      }
      onPatch({ org, contact: main })
    } catch {
      onPatch({ org: { orgId: id, name: '' }, contact: null })
    } finally { setLoading('') }
  }

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 8 }}>
        <button type="button" style={{ ...seg(kind === 'person'), borderRadius: '4px 0 0 4px' }}
          onClick={() => kind !== 'person' && onPatch({ kind: 'person', org: null, contact: null })}>Person</button>
        <button type="button" style={{ ...seg(kind === 'org'), borderRadius: '0 4px 4px 0', borderLeft: 'none' }}
          onClick={() => kind !== 'org' && onPatch({ kind: 'org', org: null, contact: null })}>Organization</button>
      </div>
      {kind === 'person' ? (
        <>
          <ContactPicker value={(c && c.contactId) || ''} name={(c && c.name) || ''} inputStyle={inputStyle} menuZIndex={menuZIndex} onChange={pickPerson} />
          {c && <LineInputs lines={PERSON_LINES} value={c} inputStyle={inputStyle} onChange={v => onPatch({ contact: v })} />}
        </>
      ) : (
        <>
          <OrganizationPicker value={(o && o.orgId) || ''} name={(o && o.name) || ''} inputStyle={inputStyle} menuZIndex={menuZIndex} onChange={pickOrg} />
          {loading && <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 6 }}>{loading}</div>}
          {o && !loading && (
            <>
              <LineInputs lines={ORG_LINES} value={o} inputStyle={inputStyle} onChange={v => onPatch({ org: v })} />
              <div style={sub}>Main contact</div>
              {c ? (
                <>
                  <LineInputs lines={MAIN_CONTACT_LINES} value={c} inputStyle={inputStyle} onChange={v => onPatch({ contact: { ...v, company: o.name || v.company } })} />
                  <button type="button" onClick={() => onPatch({ contact: null })}
                    style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#c0392b' }}>remove main contact</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 6 }}>No main contact on file. Pick one to include, or share the organization alone.</div>
                  <ContactPicker value="" name="" inputStyle={inputStyle} menuZIndex={menuZIndex}
                    onChange={(id, name, rec) => { if (id) onPatch({ contact: { ...personFromRecord(id, (rec && rec.fields) || {}, o.name), name: name || '' } }) }} />
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default function JourneyEntryModal() {
  const [open, setOpen] = useState(false)
  // Opened from outside the project (the morning check's "Add to Client Hub…"): which project, and
  // a z-index that sits above whatever opened it.
  const [projectOverride, setProjectOverride] = useState('')
  const [zOver, setZOver] = useState(null)
  const [journeyId, setJourneyId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [type, setType] = useState(ADDABLE_TYPES[0])
  // A caller that opens the modal for a specific pushed type (the milestone ⋯ menu's presetType)
  // keeps that one chip alongside the addable ones.
  const [presetType, setPresetType] = useState(null)
  // Opened from a thread in the morning check: the save is logged on that thread as an action.
  const [sourceThread, setSourceThread] = useState(null)   // { threadId, memberId } | null
  const [title, setTitle] = useState('')
  const [data, setData] = useState({})
  const [milestoneId, setMilestoneId] = useState('')
  const [meetingId, setMeetingId] = useState('')
  // Defaults to a draft for a brand-new entry (nothing reaches the client, no subscriber email,
  // until someone deliberately publishes it) — journey_entries.published otherwise defaults to
  // true at the DB level, which was silently auto-publishing every new entry the instant it was
  // saved here (no draft step ever existed in this modal). Editing an existing entry reflects
  // its current state instead of resetting it.
  const [publishNow, setPublishNow] = useState(false)
  const [saving, setSaving] = useState(false)

  // Meeting picker state — posted meetings only (only those have a minutes PDF to link).
  const [meetings, setMeetings] = useState(null)
  // The project's phases (names, in Sort Order) — the phase picker's options, kept to the project's
  // real phases so a manual placement matches the roadmap grouping.
  const [projectPhases, setProjectPhases] = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setJourneyId(d.journeyId || null)
      setProjectOverride(d.projectId || '')
      setSourceThread(d.threadId && !d.entry ? { threadId: d.threadId, memberId: d.memberId || ctx.currentMember?.id || null } : null)
      setZOver(d.zIndex || null)
      // Shortcut path: opened with a projectId but no journeyId — resolve (or create) it.
      if (!d.journeyId && d.projectId) {
        const projName = cleanName((ctx.state?.milestones || [])[0]?.fields?.['Project Name'])
        getOrCreateLink(d.projectId, d.projectName || projName)
          .then(({ journey }) => setJourneyId(journey.id))
          .catch(() => {})
      }
      const entry = d.entry || null
      if (entry) {
        setEditingId(entry.id)
        setType(entry.type)
        setPresetType(null)
        setTitle(entry.title || '')
        // Pre-populate the phase picker with the entry's current placement. Non-milestone entries
        // store phase in data.phase; if it was only inferred (e.g. the onboarding questionnaire's
        // default), seed it so the dropdown reflects where the entry actually sits and Save preserves it.
        const d0 = entry.data || {}
        const seedPhase = (!d0.phase && !entry.milestone_airtable_id && entry.type === 'onboarding-questionnaire')
          ? 'Pre-design & Planning Phase' : ''
        setData(seedPhase ? { ...d0, phase: seedPhase } : d0)
        setMilestoneId(entry.milestone_airtable_id || '')
        setMeetingId(entry.meeting_airtable_id || '')
        setPublishNow(!!entry.published)
      } else {
        setEditingId(null)
        setType(d.presetType || ADDABLE_TYPES[0])
        setPresetType(d.presetType && !ADDABLE_TYPES.includes(d.presetType) ? d.presetType : null)
        setTitle(d.presetTitle || '')
        // presetPhase = added under a phase branch → adopt that phase silently. presetData = extra
        // fields a caller fixes up front (a milestone's sketch ribbon carries data.milestoneId).
        setData({ ...(d.presetData || {}), ...(d.presetPhase ? { phase: d.presetPhase } : {}) })
        setMilestoneId(d.presetMilestoneId || '')
        setMeetingId(d.presetMeetingId || '')
        setPublishNow(false)
      }
      setMeetings(null)
      setSaving(false)
      setOpen(true)
    }
    window.addEventListener('open-journey-entry-modal', onOpen)
    return () => window.removeEventListener('open-journey-entry-modal', onOpen)
  }, [])

  const mod = getModule(type)

  // Load posted meetings when a meeting-backed type is active — only a posted meeting has a minutes
  // PDF to link (see api/_shareDocs.js#fetchMeetingRef, which only ever resolves Posted Meeting Minutes).
  useEffect(() => {
    if (!open || !mod?.usesMeeting || meetings !== null) return
    const projId = projectOverride || wf.zoomProjectId || ctx.recordId || ''
    if (!projId) { setMeetings([]); return }
    getProjectMeetings(projId)
      .then(r => setMeetings((r || []).filter(m => m.fields?.['Posted'] === true)))
      .catch(() => setMeetings([]))
  }, [open, type]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load the project's phases (for the phase picker) when the modal opens.
  useEffect(() => {
    if (!open) return
    const projId = projectOverride || wf.zoomProjectId || ctx.recordId || ''
    if (!projId) { setProjectPhases([]); return }
    getProjectPhases(projId)
      .then(recs => {
        const seen = new Set(), out = []
        ;(recs || []).forEach(r => { const n = r.fields?.['Phase Name']; if (n && !seen.has(n)) { seen.add(n); out.push(n) } })
        setProjectPhases(out)
      })
      .catch(() => setProjectPhases([]))
  }, [open])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  // Milestones grouped by deliverable type (from the loaded project state)
  const milestones = ctx.state?.milestones || []
  const msGroups = {}
  milestones.forEach(m => {
    const t = getFirst(m.fields?.['Name (from Deliverable)']) || 'Other'
    ;(msGroups[t] = msGroups[t] || []).push(m)
  })

  // Gate: a milestone may only be added to the journey once posted (delivered).
  const selectedMs = milestoneId ? milestones.find(m => m.id === milestoneId) : null
  const selectedMsPosted = selectedMs ? isMilestonePosted(selectedMs) : true

  // Phase options = the project's real phases (synced from the Phases table).
  const phaseOptions = projectPhases || []
  const usesMs = !!mod?.usesMilestone
  const usesMeeting = !!mod?.usesMeeting
  const inferredPhase = selectedMs ? (getFirst(selectedMs.fields?.['Project Phase']) || '') : ''
  // Selected meeting → its phase (normalized to the project's real Phase Name — see
  // matchPhaseName in services/phases.js) + its own date. Same "inferred, not user-editable" UX as
  // a milestone-backed entry, just resolved from the Meeting record instead of the Deliverable one.
  const selectedMeeting = meetingId ? (meetings || []).find(m => m.id === meetingId) : null
  const meetingRawPhase = selectedMeeting ? (selectedMeeting.fields?.['Phase'] || '') : ''
  const meetingInferredPhase = meetingRawPhase ? matchPhaseName(meetingRawPhase, phaseOptions) : ''
  const meetingInferredDate = selectedMeeting ? (selectedMeeting.fields?.['Meeting Date'] || '') : ''
  // Milestone- and meeting-backed entries infer their phase + date; everything else needs a phase
  // chosen here.
  const needsPhase = !usesMs && !usesMeeting

  function setField(key, value) { setData(prev => ({ ...prev, [key]: value })) }

  // A NEW "Plans & 3D model" is made in AlderCAD, not saved here (an existing one edits normally).
  const launching = type === 'client_release' && !editingId
  function openAlderCad() {
    const projectId = projectOverride || wf.zoomProjectId || ctx.recordId || ''
    const q = new URLSearchParams({ conceptId: data.conceptId || '', projectId, release: 'client', phase: data.phase || '' })
    window.open('/modeler?' + q.toString(), '_blank')
    setOpen(false)
  }

  async function handleSave() {
    if (!journeyId) return
    setSaving(true)
    const selectedMsFields = milestoneId ? milestones.find(m => m.id === milestoneId)?.fields : null
    const msType = selectedMsFields ? (getFirst(selectedMsFields['Name (from Deliverable)']) || null) : null
    const msTypeId = selectedMsFields ? (getFirst(selectedMsFields['Deliverable']) || null) : null
    // Meeting-backed entries don't expose an editable phase/date field (like milestone-backed ones)
    // — bake the inferred values into data at save time instead.
    const dataToSave = usesMeeting ? { ...data, phase: meetingInferredPhase, entryDate: meetingInferredDate } : data
    try {
      await upsertEntry({
        id: editingId || undefined,
        journeyId,
        type,
        title: title.trim() || null,
        data: dataToSave,
        milestoneAirtableId: mod?.usesMilestone ? (milestoneId || null) : null,
        milestoneType: mod?.usesMilestone ? msType : null,
        milestoneTypeId: mod?.usesMilestone ? msTypeId : null,
        meetingAirtableId: usesMeeting ? (meetingId || null) : null,
        published: publishNow,
      })
      if (sourceThread && !editingId) {
        // Best-effort: the entry is saved either way; a failed log never blocks it.
        const what = (mod && mod.label) || 'an entry'
        const who = type === 'contact_card' ? ((data.kind === 'org' && data.org && data.org.name) || (data.contact && data.contact.name) || title.trim()) : title.trim()
        const note = 'Added to the Client Hub: ' + what + (who ? ' (' + who + ')' : '') +
          (publishNow ? '. Published to the client.' : '. Saved as a draft, not yet visible to the client.')
        try { await logClientHubAction(sourceThread.threadId, sourceThread.memberId, note) } catch (e) { console.error('[JourneyEntryModal] thread log failed', e) }
      }
      window.dispatchEvent(new CustomEvent('journey-updated', { detail: { journeyId } }))
      setOpen(false)
    } catch (err) {
      console.error('[JourneyEntryModal] save failed', err)
      setSaving(false)
    }
  }

  const canSave = !!type && !!journeyId &&
    (!mod?.usesMilestone || (!!milestoneId && selectedMsPosted)) &&
    (!usesMeeting || !!meetingId) &&
    (!needsPhase || !!data.phase) &&   // non-milestone, non-meeting entries must be placed in a phase
    (type !== 'document' || !!(data.pdf && data.pdf.url)) &&
    (type !== 'contact_card' || (data.kind === 'org' ? !!(data.org && (data.org.name || '').trim()) : !!(data.contact && (data.contact.name || '').trim())))

  return createPortal(
    <div style={zOver ? { ...S.scrim, zIndex: zOver } : S.scrim}>
      <div data-modal-id="JourneyEntryModal" style={S.modal}>
        <div style={S.head}>
          <span style={S.headTitle}>{editingId ? 'Edit entry' : 'Add entry'}</span>
          <button onClick={() => setOpen(false)} style={S.x}>×</button>
        </div>

        <div style={S.body}>
          {/* Type */}
          <label style={S.label}>Type</label>
          <div style={S.typeGrid}>
            {(editingId ? [type] : (presetType ? [presetType, ...ADDABLE_TYPES] : ADDABLE_TYPES)).filter(t => MODULE_REGISTRY[t]).map(t => {
              const m = MODULE_REGISTRY[t]
              const active = t === type
              return (
                <button key={t} onClick={() => setType(t)}
                  style={{ ...S.typeChip, ...(active ? S.typeChipActive : null) }}>
                  <span style={{ fontSize: 13 }}>{m.icon}</span>
                  {m.label}{m.stub ? ' ·' : ''}
                </button>
              )
            })}
          </div>

          {launching && (
            <>
              <label style={S.label}>Model</label>
              <ReleaseModelField data={data} projectId={projectOverride || wf.zoomProjectId || ctx.recordId || ''} onChange={patch => setData(prev => ({ ...prev, ...patch }))} />
              <div style={S.hint}>
                AlderCAD opens in a new tab on the release form, set to the phase below. Pick the drawings
                there and release; the card lands on this Hub with the plans and the model to walk, frozen
                as they are when you release. Keep that tab open until it finishes.
              </div>
            </>
          )}

          {!launching && <>
          {/* Title */}
          <label style={S.label}>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Optional — shown as the heading"
            style={S.input} />

          {/* Draft/publish — defaults to draft for a new entry so nothing reaches the client (and
              no subscriber email fires) until it's been checked. Toggle back to draft later from
              this same modal (Edit) or the outline row's ⋯ menu. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={publishNow} onChange={e => setPublishNow(e.target.checked)} style={{ width: 15, height: 15 }} />
            <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>Publish immediately</span>
          </label>
          <div style={S.hint}>
            {publishNow
              ? 'Visible to the client as soon as you save. Subscribers get an email notification.'
              : 'Saved as a draft — only your team can see it. Publish it later from Edit or the ⋯ menu once it’s ready.'}
          </div>
          </>}

          {/* Milestone picker */}
          {mod?.usesMilestone && (
            <>
              <label style={S.label}>Milestone</label>
              <select value={milestoneId} onChange={e => setMilestoneId(e.target.value)} style={S.input}>
                <option value="">Select a milestone…</option>
                {Object.keys(msGroups).sort().map(grp => (
                  <optgroup key={grp} label={grp}>
                    {msGroups[grp].map(m => {
                      const posted = isMilestonePosted(m)
                      const name = getFirst(m.fields?.['Full Deliverable Name']) || getFirst(m.fields?.['Name']) || m.id
                      return (
                        <option key={m.id} value={m.id} disabled={!posted}>
                          {name}{posted ? '' : ' — not posted yet'}
                        </option>
                      )
                    })}
                  </optgroup>
                ))}
              </select>
              {milestoneId && !selectedMsPosted && (
                <div style={S.warn}>
                  This milestone hasn&rsquo;t been posted yet. Only delivered work can appear on the
                  client&rsquo;s hub — post the milestone first, then add it here (or add it right
                  from the post dialog).
                </div>
              )}
            </>
          )}

          {/* Meeting picker — posted meetings only (only those have a minutes PDF to link). For
              backfilling a meeting posted before this feature existed, or skipped at post time. */}
          {usesMeeting && (
            <>
              <label style={S.label}>Meeting</label>
              {meetings === null ? (
                <div style={S.hint}>Loading posted meetings…</div>
              ) : meetings.length === 0 ? (
                <div style={S.hint}>No posted meetings yet — post a meeting's minutes first, then add it here (or add it right from the post dialog).</div>
              ) : (
                <select value={meetingId} onChange={e => setMeetingId(e.target.value)} style={S.input}>
                  <option value="">Select a meeting…</option>
                  {meetings.map(m => {
                    const label = [m.fields?.['Type'], m.fields?.['Meeting Date String'] || m.fields?.['Meeting Date']].filter(Boolean).join(' — ')
                    return <option key={m.id} value={m.id}>{label || m.id}</option>
                  })}
                </select>
              )}
            </>
          )}

          {/* Phase — the roadmap's organizing branch. Inferred from the milestone/meeting when
              present, otherwise chosen here (required, so an entry is never unplaced). */}
          <label style={S.label}>Phase</label>
          {usesMs ? (
            <div style={S.hint}>{inferredPhase ? 'Inferred from milestone — ' + inferredPhase : 'Inferred from the selected milestone.'}</div>
          ) : usesMeeting ? (
            <div style={S.hint}>{meetingInferredPhase ? 'Inferred from meeting — ' + meetingInferredPhase : 'Inferred from the selected meeting.'}</div>
          ) : projectPhases === null ? (
            <div style={S.hint}>Loading phases…</div>
          ) : (
            <select value={data.phase || ''} onChange={e => setField('phase', e.target.value)} style={S.input}>
              <option value="">Select a phase…</option>
              {phaseOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}

          {!launching && <>
          {/* Date — the milestone's post date when milestone-backed, the meeting's own date when
              meeting-backed, else set here. */}
          <label style={S.label}>Date</label>
          {usesMs ? (
            <div style={S.hint}>Uses the milestone&rsquo;s post date.</div>
          ) : usesMeeting ? (
            <div style={S.hint}>Uses the meeting&rsquo;s date.</div>
          ) : (
            <input type="date" value={data.entryDate || ''} onChange={e => setField('entryDate', e.target.value)} style={S.input} />
          )}

          {/* Per-type fields from the registry */}
          {(mod?.authoringFields || []).map(f => (
            <div key={f.key}>
              <label style={S.label}>{f.label}</label>
              {f.type === 'sketches' ? (
                <SketchManager value={data[f.key]} onChange={v => setField(f.key, v)} projectId={projectOverride || wf.zoomProjectId || ctx.recordId || ''} />
              ) : f.type === 'contactCard' ? (
                <ContactCardField data={data} inputStyle={S.input} menuZIndex={Math.max(9000, (zOver || 0) + 10)} onPatch={patch => setData(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }))} />
              ) : f.type === 'pdfDocument' ? (
                <PdfDocumentField value={data[f.key]} journeyId={journeyId} title={title} onTitle={setTitle} onChange={v => setField(f.key, v)} />
              ) : f.type === 'walkthroughModel' ? (
                <WalkthroughModelField data={data} projectId={projectOverride || wf.zoomProjectId || ctx.recordId || ''} onChange={patch => setData(prev => ({ ...prev, ...patch }))} />
              ) : f.type === 'text' || f.type === 'url' ? (
                <input value={data[f.key] || ''} onChange={e => setField(f.key, e.target.value)}
                  placeholder={f.placeholder || ''} style={S.input} />
              ) : f.type === 'list' ? (
                <textarea value={(Array.isArray(data[f.key]) ? data[f.key].join('\n') : (data[f.key] || ''))}
                  onChange={e => setField(f.key, e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                  placeholder={f.placeholder || ''} style={{ ...S.input, minHeight: 70, resize: 'vertical' }} />
              ) : (
                <textarea value={data[f.key] || ''} onChange={e => setField(f.key, e.target.value)}
                  placeholder={f.placeholder || ''} style={{ ...S.input, minHeight: 90, resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }} />
              )}
            </div>
          ))}

          </>}

          {mod?.stub && (
            <div style={S.stubNote}>
              This entry type can be authored now; its client-facing view is coming soon.
            </div>
          )}
        </div>

        <div style={S.foot}>
          <button onClick={() => setOpen(false)} style={S.cancel}>Cancel</button>
          {launching ? (
            <button onClick={openAlderCad} disabled={!data.modelId || !data.conceptId || !data.phase} style={{ ...S.save, opacity: (!data.modelId || !data.conceptId || !data.phase) ? 0.5 : 1 }}>
              Open AlderCAD to release
            </button>
          ) : (
          <button onClick={handleSave} disabled={!canSave || saving} style={{ ...S.save, opacity: (!canSave || saving) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add entry')}
          </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

const S = {
  scrim: { position: 'fixed', inset: 0, zIndex: 820, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { width: 560, maxWidth: '94vw', maxHeight: '88vh', background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', boxShadow: '0 16px 48px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 },
  headTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 20, lineHeight: 1 },
  body: { padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  label: { display: 'flex', alignItems: 'center', gap: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', margin: '14px 0 6px' },
  input: { width: '100%', padding: '9px 11px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: 'var(--aj-text)', outline: 'none', boxSizing: 'border-box', background: 'var(--aj-bg-subtle)' },
  typeGrid: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  typeChip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-subtle)', color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  typeChipActive: { background: '#07272D', color: '#fff', border: '0.5px solid #07272D' },
  toggle: { marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontSize: 11, fontWeight: 600, color: '#1a6fa8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  hint: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' },
  warn: { marginTop: 8, padding: '9px 12px', borderRadius: 8, background: 'rgba(184,84,10,0.13)', color: '#b8540a', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5 },
  stubNote: { marginTop: 16, padding: '10px 12px', borderRadius: 8, background: 'rgba(184,84,10,0.13)', color: '#b8540a', fontFamily: '"DM Sans",sans-serif', fontSize: 12 },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 },
  cancel: { padding: '9px 18px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  save: { padding: '9px 20px', borderRadius: 20, border: 'none', background: '#07272D', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
}
